import { useCallback } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { Tasks } from '@air/api';
import { type Board, type TaskArgs } from '@air/api/types';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useCreateTask } from '~/utils/taskUtils/useCreateTask';

export type CreatePrivateAssetBoardMembershipUpdateTaskParams = {
  boardId: string;
  includeDescendants: boolean;
  operation:
    | {
        type: 'add';
        targetBoards: Pick<Board, 'id' | 'title'>[];
      }
    | {
        type: 'move';
        targetBoard: Pick<Board, 'id' | 'title' | 'ancestors'>;
      };
  totalSelectedItemsCount: number;
  trackLocation?: AirActionTrackingLocation;
};

export const useCreatePrivateAssetBoardMembershipUpdateTask = () => {
  const { createTask } = useCreateTask();
  const { currentWorkspace } = useCurrentWorkspace();

  const createPrivateAssetBoardMembershipUpdateTask = useCallback(
    async ({
      boardId,
      includeDescendants,
      operation,
      totalSelectedItemsCount,
      trackLocation,
    }: CreatePrivateAssetBoardMembershipUpdateTaskParams) => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      const args: TaskArgs<'AssetBoardMembershipUpdater'>['args'] =
        operation.type === 'add'
          ? {
              boardId,
              includeDescendants,
              operation: {
                type: operation.type,
                targetBoardIds: operation.targetBoards.map(({ id }) => id),
              },
            }
          : {
              boardId,
              includeDescendants,
              operation: {
                type: operation.type,
                targetBoardId: operation.targetBoard.id,
              },
            };

      const metadataOperation =
        operation.type === 'add'
          ? {
              operation: operation.type,
              targetBoards: operation.targetBoards.map(({ id, title }) => ({ id, title })),
            }
          : {
              operation: operation.type,
              targetBoard: {
                ancestors: operation.targetBoard.ancestors?.map(({ id, title }) => ({ id, title })),
                id: operation.targetBoard.id,
                title: operation.targetBoard.title,
              },
            };

      return createTask({
        task: {
          metadata: {
            boardId,
            includeDescendants,
            ...metadataOperation,
            totalSelectedItemsCount,
            trackLocation,
          },
          type: 'AssetBoardMembershipUpdate',
          workspaceId,
        },
        createTask: () =>
          Tasks.createTask({
            workspaceId,
            task: {
              type: 'AssetBoardMembershipUpdater',
              args,
            },
          }),
      });
    },
    [createTask, currentWorkspace?.id],
  );

  return { createPrivateAssetBoardMembershipUpdateTask };
};
